import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, Section } from "@/components/Shared";
import { toast } from "sonner";

export default function MySalary() {
  const [s, setS] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [loans, setLoans] = useState([]);
  const [latestSlip, setLatestSlip] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/salary-structure/me"),
      api.get("/salary-revisions"),
      api.get("/loans"),
      api.get("/payslips/me"),
    ]).then(([a, b, c, d]) => {
      setS(a.data); setRevisions(b.data); setLoans(c.data); setLatestSlip(d.data[0] || null);
    }).catch((e) => toast.error(formatErr(e)));
  }, []);

  const downloadLatest = async () => {
    if (!latestSlip) return toast.error("No payslip available yet");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payslips/me/${latestSlip.year}/${latestSlip.month}/pdf`, {
      credentials: "include",
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank"); URL.revokeObjectURL(url);
  };

  if (!s) return <div className="text-neutral-500">Loading…</div>;
  if (!s.basic_salary) return (
    <div data-testid="my-salary">
      <PageHeader overline="Compensation" title="My Salary" />
      <div className="bg-white border border-[#E5E5E5] p-10 text-center text-neutral-500">Your salary structure has not been set up yet.</div>
    </div>
  );

  return (
    <div data-testid="my-salary">
      <PageHeader overline="Compensation" title="My Salary"
        subtitle="Your current salary structure, revisions and estimated monthly take-home."
        actions={
          <button onClick={downloadLatest} data-testid="dl-latest-slip" className="h-10 px-4 border border-[#1F4E8C] text-[#1F4E8C] hover:bg-[#1F4E8C] hover:text-white transition-colors text-sm font-semibold">
            Download Latest Payslip
          </button>
        } />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#1F4E8C] text-white p-6 lg:col-span-2">
          <div className="eyebrow text-sky-200">Monthly Gross</div>
          <div className="font-head text-5xl lg:text-6xl font-light tracking-tighter mt-2">
            {s.currency} {(s.gross || 0).toLocaleString()}
          </div>
          <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-white/20">
            <div><div className="eyebrow text-sky-200">Basic</div><div className="font-head text-xl font-semibold mt-1">₹{s.basic_salary.toLocaleString()}</div></div>
            <div><div className="eyebrow text-sky-200">Deductions</div><div className="font-head text-xl font-semibold mt-1">₹{(s.total_deductions || 0).toLocaleString()}</div></div>
            <div><div className="eyebrow text-sky-200">Net Estimate</div><div className="font-head text-xl font-semibold mt-1 text-[#FF5500]">₹{(s.net_estimate || 0).toLocaleString()}</div></div>
          </div>
          <div className="text-xs text-sky-200 mt-4">Effective from {s.effective_from} · Estimates exclude variable earnings and attendance-based adjustments.</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-6">
          <div className="eyebrow mb-3">Active Loans</div>
          {loans.filter(l => l.status === "active").length === 0 && <div className="text-sm text-neutral-500">No active loans or advances.</div>}
          {loans.filter(l => l.status === "active").map((l) => (
            <div key={l.id} className="pb-3 mb-3 border-b border-[#F0F0EE] last:border-0 last:mb-0 last:pb-0">
              <div className="text-sm font-medium">{l.loan_type} · ₹{l.principal.toLocaleString()}</div>
              <div className="text-xs text-neutral-500">Monthly: ₹{l.monthly_deduction.toLocaleString()}</div>
              <div className="text-xs text-neutral-500">Remaining: ₹{l.remaining?.toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Earnings">
          <table className="w-full table-dense">
            <thead><tr className="border-b-2 border-[#1F4E8C] text-left"><th className="eyebrow">Component</th><th className="eyebrow">Amount</th></tr></thead>
            <tbody>
              <tr className="border-b border-[#F0F0EE]"><td className="font-medium">Basic Salary</td><td className="font-semibold">₹{s.basic_salary.toLocaleString()}</td></tr>
              {(s.earnings || []).map((e, i) => (
                <tr key={i} className="border-b border-[#F0F0EE]"><td>{e.name}</td><td className="font-semibold">₹{e.amount?.toLocaleString()}</td></tr>
              ))}
              <tr className="border-t-2 border-[#111]"><td className="font-head font-bold">Gross</td><td className="font-head font-bold text-[#1F4E8C]">₹{(s.gross || 0).toLocaleString()}</td></tr>
            </tbody>
          </table>
        </Section>
        <Section title="Deductions">
          <table className="w-full table-dense">
            <thead><tr className="border-b-2 border-[#1F4E8C] text-left"><th className="eyebrow">Component</th><th className="eyebrow">Amount</th></tr></thead>
            <tbody>
              {(s.deductions || []).map((e, i) => (
                <tr key={i} className="border-b border-[#F0F0EE]"><td>{e.name}</td><td className="font-semibold">₹{e.amount?.toLocaleString()}</td></tr>
              ))}
              <tr className="border-t-2 border-[#111]"><td className="font-head font-bold">Total Deductions</td><td className="font-head font-bold">₹{(s.total_deductions || 0).toLocaleString()}</td></tr>
            </tbody>
          </table>
        </Section>
      </div>

      <div className="mt-4">
        <Section title="Salary History">
          <table className="w-full table-dense">
            <thead><tr className="border-b-2 border-[#1F4E8C] text-left"><th className="eyebrow">Effective</th><th className="eyebrow">Previous</th><th className="eyebrow">New Basic</th><th className="eyebrow">Change</th><th className="eyebrow">Reason</th></tr></thead>
            <tbody>
              {revisions.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-neutral-500">No revisions yet</td></tr>}
              {revisions.map((r) => (
                <tr key={r.id} className="border-b border-[#F0F0EE]">
                  <td>{r.effective_date}</td>
                  <td>₹{r.previous_basic?.toLocaleString()}</td>
                  <td className="font-semibold">₹{r.new_basic?.toLocaleString()}</td>
                  <td className={r.percent_change > 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>{r.percent_change}%</td>
                  <td className="text-xs">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>
    </div>
  );
}
